import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { CSpinner } from '@coreui/react';
import api from '../../services/api';
import { saveCustomerSession, isCustomerLoggedIn } from '../../services/customerAuth';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

export default function CustomerLoginPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('login'); // 'login' | 'otp'
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const googleBtnRef = useRef(null);

  const handleGoogleLogin = useCallback(async (credential) => {
    setError('');
    setLoading(true);
    try {
      const { data } = await api.post('/customer/google/login', { credential });
      saveCustomerSession(data.token, data.customer);
      navigate('/customer/dashboard', { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || 'Google sign-in failed.');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (tab !== 'login' || !GOOGLE_CLIENT_ID) return;

    function renderBtn() {
      if (!window.google || !googleBtnRef.current) return;
      const w = googleBtnRef.current.getBoundingClientRect().width || 300;
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'signin_with',
        shape: 'rectangular',
        logo_alignment: 'left',
        width: Math.floor(w),
      });
    }

    function initGoogle() {
      if (!window.google || !googleBtnRef.current) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: (resp) => { handleGoogleLogin(resp.credential); },
        auto_select: false,
      });
      requestAnimationFrame(renderBtn);
    }

    let ro;
    if (typeof ResizeObserver !== 'undefined' && googleBtnRef.current) {
      ro = new ResizeObserver(() => { if (window.google) renderBtn(); });
      ro.observe(googleBtnRef.current);
    }

    if (typeof window.google !== 'undefined') {
      initGoogle();
      return () => ro && ro.disconnect();
    }

    const existing = document.getElementById('google-gsi-script');
    if (existing) {
      existing.addEventListener('load', initGoogle);
      return () => {
        existing.removeEventListener('load', initGoogle);
        ro && ro.disconnect();
      };
    }

    const script = document.createElement('script');
    script.id = 'google-gsi-script';
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = initGoogle;
    document.head.appendChild(script);
    return () => ro && ro.disconnect();
  }, [tab, handleGoogleLogin]);

  useEffect(() => {
    if (isCustomerLoggedIn()) navigate('/customer/dashboard', { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  async function handleLogin(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await api.post('/customer/login', { phone: phone.trim(), password });
      saveCustomerSession(data.token, data.customer);
      navigate('/customer/dashboard', { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Please try again.');
    } finally { setLoading(false); }
  }

  async function handleSendOtp(e) {
    e && e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.post('/customer/send-otp', { phone: phone.trim(), purpose: 'login' });
      setOtpSent(true);
      setCountdown(600);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send OTP.');
    } finally { setLoading(false); }
  }

  async function handleOtpLogin(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.post('/customer/verify-otp', { phone: phone.trim(), otp: otp.trim(), purpose: 'login' });
      // After OTP login, get a session token via login with temp (future: issue token on verify)
      // For now redirect to set password flow — here we simply alert success
      // TODO: backend should return token on OTP login too
      setError('OTP verified. Please use password login or register an account.');
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid OTP.');
    } finally { setLoading(false); }
  }

  const inp = { width: '100%', padding: '11px 14px', border: '1px solid #dde1e7', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', outline: 'none', marginBottom: 14 };
  const btn = { width: '100%', padding: '12px', background: '#8bc34a', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer' };

  return (
    <div style={{ minHeight: '100vh', background: '#f4f6f9', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #071e2b, #0d3a4a)', padding: '20px 24px' }}>
        <div style={{ maxWidth: 480, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <img src={`${process.env.PUBLIC_URL}/images/fmzlogo.png`} alt="FMZ" style={{ height: 40, objectFit: 'contain' }} />
            <div style={{ color: '#8bc34a', fontWeight: 700, fontSize: 16 }}>FMZ Points</div>
          </div>
          <Link to="/track" style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, textDecoration: 'none' }}>← Track Repair</Link>
        </div>
      </div>

      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 4px 24px rgba(0,0,0,0.1)', width: '100%', maxWidth: 420, overflow: 'hidden' }}>
          <div style={{ padding: '28px 28px 0' }}>
            <div style={{ fontWeight: 700, fontSize: 22, color: '#0d3a4a', marginBottom: 4 }}>Sign In</div>
            <div style={{ fontSize: 13, color: '#6c757d', marginBottom: 24 }}>Access your FMZ Points account</div>

            {/* Tabs */}
            <div style={{ display: 'flex', borderBottom: '2px solid #f0f0f0', marginBottom: 24 }}>
              {[['login', 'Password'], ['otp', 'OTP Login']].map(([key, label]) => (
                <button key={key} onClick={() => { setTab(key); setError(''); setOtpSent(false); }}
                  style={{ flex: 1, padding: '8px 0', background: 'none', border: 'none', fontWeight: tab === key ? 700 : 400, color: tab === key ? '#8bc34a' : '#6c757d', borderBottom: tab === key ? '2px solid #8bc34a' : '2px solid transparent', marginBottom: -2, cursor: 'pointer', fontSize: 14 }}>
                  {label}
                </button>
              ))}
            </div>

            {error && (
              <div style={{ background: '#fff3f3', border: '1px solid #f5c6cb', borderRadius: 6, padding: '10px 14px', color: '#721c24', fontSize: 13, marginBottom: 16 }}>
                {error}
              </div>
            )}

            {tab === 'login' && (
              <>
                <form onSubmit={handleLogin}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>Phone Number</label>
                  <input style={inp} type="text" value={phone} onChange={e => setPhone(e.target.value)} placeholder="e.g. 0123456789" required />
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>Password</label>
                  <input style={inp} type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Your password" required />
                  <button type="submit" style={loading ? { ...btn, opacity: 0.6 } : btn} disabled={loading}>
                    {loading ? <CSpinner size="sm" style={{ color: '#fff' }} /> : 'Sign In'}
                  </button>
                </form>
                {GOOGLE_CLIENT_ID ? (
                  <div style={{ marginTop: 20 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                      <div style={{ flex: 1, height: 1, background: '#e8ecf0' }} />
                      <span style={{ fontSize: 12, color: '#8c9bab', fontWeight: 600 }}>OR</span>
                      <div style={{ flex: 1, height: 1, background: '#e8ecf0' }} />
                    </div>
                    <div ref={googleBtnRef} style={{ display: 'flex', justifyContent: 'center', minHeight: 44 }} />
                    <p style={{ marginTop: 12, fontSize: 11, color: '#8c9bab', textAlign: 'center', lineHeight: 1.4 }}>
                      First time? Sign in with phone and password, then link Google from My Profile.
                    </p>
                  </div>
                ) : (
                  <p style={{ marginTop: 16, fontSize: 12, color: '#8c9bab', textAlign: 'center' }}>
                    Google sign-in is not configured for this site.
                  </p>
                )}
              </>
            )}

            {tab === 'otp' && !otpSent && (
              <form onSubmit={handleSendOtp}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>Phone Number</label>
                <input style={inp} type="text" value={phone} onChange={e => setPhone(e.target.value)} placeholder="e.g. 0123456789" required />
                <button type="submit" style={loading ? { ...btn, opacity: 0.6 } : btn} disabled={loading}>
                  {loading ? <CSpinner size="sm" style={{ color: '#fff' }} /> : 'Send OTP'}
                </button>
              </form>
            )}

            {tab === 'otp' && otpSent && (
              <form onSubmit={handleOtpLogin}>
                <div style={{ fontSize: 13, color: '#6c757d', marginBottom: 14 }}>
                  OTP sent to <strong>{phone}</strong>.{' '}
                  {countdown > 0 && <span style={{ color: '#8bc34a' }}>Expires in {Math.floor(countdown / 60)}:{String(countdown % 60).padStart(2, '0')}</span>}
                </div>
                <input style={{ ...inp, textAlign: 'center', fontSize: 24, letterSpacing: 8, fontWeight: 700 }}
                  type="text" inputMode="numeric" value={otp}
                  onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000" maxLength={6} required />
                <button type="submit" style={loading || otp.length < 6 ? { ...btn, opacity: 0.6 } : btn} disabled={loading || otp.length < 6}>
                  {loading ? <CSpinner size="sm" style={{ color: '#fff' }} /> : 'Verify & Login'}
                </button>
                <button type="button" onClick={handleSendOtp} disabled={countdown > 540}
                  style={{ marginTop: 10, width: '100%', background: 'none', border: 'none', color: '#8bc34a', fontSize: 13, cursor: 'pointer', textDecoration: 'underline' }}>
                  Resend OTP
                </button>
              </form>
            )}

            <div style={{ textAlign: 'center', padding: '20px 0 4px', fontSize: 13, color: '#6c757d' }}>
              Don't have an account?{' '}
              <Link to="/track" style={{ color: '#8bc34a', fontWeight: 600, textDecoration: 'none' }}>Track your repair & register</Link>
            </div>
          </div>
          <div style={{ padding: '0 28px 28px' }} />
        </div>
      </div>
    </div>
  );
}
